import axios from 'axios';
import type { ErrorCategory, OutboxStatus } from './types';

export interface ClassifiedFailure {
  category: ErrorCategory;
  code: string;
  outboxStatus: OutboxStatus;
  retryable: boolean;
  retryAfterMs: number | null;
  message: string;
}

export function classifySyncFailure(error: unknown): ClassifiedFailure {
  if (!axios.isAxiosError(error)) {
    return { category: 'UNKNOWN', code: 'UNKNOWN_ERROR', outboxStatus: 'FAILED_RETRYABLE', retryable: true, retryAfterMs: null, message: error instanceof Error ? error.message : 'Unknown synchronization failure' };
  }
  const status = error.response?.status;
  const response: any = error.response?.data;
  const message = response?.error || error.message || 'Synchronization failed';
  const retryAfterHeader =
    error.response?.headers?.['retry-after'];

  const rateLimitResetHeader =
    error.response?.headers?.['x-ratelimit-reset'];

  let retryAfterMs: number | null = null;

  if (retryAfterHeader != null) {
    const numericSeconds =
      Number(retryAfterHeader);

    if (Number.isFinite(numericSeconds)) {
      retryAfterMs =
        Math.max(0, numericSeconds * 1000);
    } else {
      const retryDate =
        Date.parse(String(retryAfterHeader));

      if (Number.isFinite(retryDate)) {
        retryAfterMs =
          Math.max(0, retryDate - Date.now());
      }
    }
  }

  if (
    retryAfterMs === null &&
    rateLimitResetHeader != null
  ) {
    const resetEpochSeconds =
      Number(rateLimitResetHeader);

    if (Number.isFinite(resetEpochSeconds)) {
      retryAfterMs =
        Math.max(
          0,
          resetEpochSeconds * 1000 - Date.now(),
        );
    }
  }

  if (!error.response) return { category: 'NETWORK', code: error.code || 'NETWORK_ERROR', outboxStatus: 'FAILED_RETRYABLE', retryable: true, retryAfterMs: null, message };
  if (status === 401) return { category: 'AUTH', code: 'AUTH_REQUIRED', outboxStatus: 'FAILED_RETRYABLE', retryable: true, retryAfterMs: null, message };
  if (status === 403) return { category: 'AUTH', code: 'PERMISSION_DENIED', outboxStatus: 'FAILED_PERMANENT', retryable: false, retryAfterMs: null, message };
  if (status === 409) return { category: 'CONFLICT', code: response?.code || 'CONFLICT', outboxStatus: 'CONFLICT', retryable: false, retryAfterMs: null, message };
  if (status === 422) return { category: 'VALIDATION', code: response?.code || 'VALIDATION_REJECTED', outboxStatus: 'FAILED_PERMANENT', retryable: false, retryAfterMs: null, message };
  if (status === 429) return {
    category: 'SERVER',
    code: response?.code || 'RATE_LIMITED',
    outboxStatus: 'FAILED_RETRYABLE',
    retryable: true,
    retryAfterMs,
    message,
  };
  if (status === 502 || status === 503 || status === 504 || (status !== undefined && status >= 500)) return { category: 'SERVER', code: `HTTP_${status}`, outboxStatus: 'FAILED_RETRYABLE', retryable: true, retryAfterMs: null, message };
  if (status === 400 && /schema|version|unsupported client/i.test(message)) return { category: 'MIGRATION', code: 'SCHEMA_INCOMPATIBLE', outboxStatus: 'FAILED_PERMANENT', retryable: false, retryAfterMs: null, message };
  return { category: 'SERVER', code: `HTTP_${status || 0}`, outboxStatus: 'FAILED_PERMANENT', retryable: false, retryAfterMs: null, message };
}

export function nextBackoffMs(attempt: number, retryAfterMs: number | null = null): number {
  if (retryAfterMs !== null) return Math.max(0, retryAfterMs);
  const boundedAttempt = Math.max(0, Math.min(attempt, 8));
  const base = 1000 * (2 ** boundedAttempt);
  const jitter = Math.floor(Math.random() * Math.max(250, base * 0.25));
  return Math.min(5 * 60 * 1000, base + jitter);
}
